import Decimal from 'decimal.js';

/**
 * Amount pound value formatter. It include minus(-) if value is negative and plus(+) if value is positive
 * @param valuePence string amount in pence
 * @param showSign
 * @returns string amount in GBP pound as £#.00 | + £#.00 | -£#.00
 */
export const amountSignedPound = (
  valuePence: number,
  showSign: 'auto' | 'always' | 'exceptZero' | 'never' | 'onlyNegative' = 'exceptZero',
): string => {
  if (isNaN(valuePence)) {
    return '£0.00';
  }
  const valuePound = new Decimal(valuePence).dividedBy(100);

  if (showSign === 'onlyNegative') {
    const absValuePound = valuePound.abs();
    const formattedAbsValue = new Intl.NumberFormat('en-UK', {
      currency: 'GBP',
      style: 'currency',
      signDisplay: 'never',
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }).format(absValuePound.toNumber());

    return valuePound.isNegative() ? `-${formattedAbsValue}` : formattedAbsValue;
  }

  return Intl.NumberFormat('en-UK', {
    currency: 'GBP',
    style: 'currency',
    signDisplay: showSign,
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(valuePound.toNumber());
};

/**
 * Amount pound value formatter. It includes minus if value is negative
 * @param valuePence string amount in pence
 * @returns string amount in GBP pound as £#.00 | £#.00 | -£#.00
 */
export const amountPound = (valuePence: number): string => {
  return amountSignedPound(valuePence, 'auto');
};

// TODO: Test and remove the other functions below here

/**
 * Amount pound value formatter
 * @param value string amount in pence
 * @returns string amount in GBP pound as £#.00
 */

/**
 * Currency conversion GBP pound to pence
 * @param value GBP Pound
 * @returns Pence value
 */
export const amountInPence = (value: number): number => {
  if (value) {
    return parseInt((value * 100).toFixed(2));
  }
  return 0;
};
/**
 * Currency conversion GBP pound symbol to Pound string
 * @param value string GBP Pound symbol
 * @returns Pound value
 */

export const inputInPound = (value: string): string => {
  if (value) {
    return value.replace(/[^0-9]+/g, '');
  }
  return '0';
};

/**
 * Currency conversion Pence to GBP pound
 * @param value in Pence
 * @returns GBP pound value
 */
export const amountInPound = (value: number): string => {
  if (!value) {
    return '0.00';
  }
  return (value / 100).toFixed(2);
};

/**
 * Input amount pound value formatter
 * @param value string amount in pound
 * @returns string amount in GBP pound as £#.00
 */
export const amountFormatter = (value: string = ''): string => {
  if (value) {
    let mValue = '';
    if (value.includes('-')) {
      mValue = '-';
    }
    const mAmount = value.replace(/[^0-9.]+/g, '');
    return mAmount.length > 0 ? `${mValue}£${mAmount}` : `${mValue}`;
  }
  return '';
};

/**
 * Currency conversion GBP pound to pence
 * @param value string GBP Pound
 * @returns Pence value
 */
export const inputInPence = (value: string): number => {
  // Return 0 if an empty string has been passed in
  if (!value) return 0;

  // Remove '£' and 'p' and any other non-essential characters while keeping '-' and '.'
  const sanitizedValue = value.replace(/[^\d.-]/g, '');

  // Construct a new Decimal.js number and convert it to pence
  return new Decimal(sanitizedValue).mul(100).toNumber();
};

export const inputNumberInPence = (value: number) => new Decimal(value).mul(100).toNumber();

/**
 * Parse currency formatted value into amount value
 * @param value string currency formatted value £#.00 | £#.00 | -£#.00
 * @returns amount number value as #.00
 */
export const currencyAmount = (value: string): number => {
  if (value) {
    const multiplier = value.includes('-') ? -1 : 1;
    return Number(value.replace(/[^0-9.]+/g, '')) * multiplier;
  }
  return 0;
};

export const RemoveSignAmount = (value: string): number => {
  if (value) {
    const newValue = Number(value.replace('£', ''));
    return newValue;
  }
  return 0;
};
/**
 * Display style
 * @param amount number currency value
 * @returns
 */
export const currencyStyle = (amount: number): string => {
  if (amount === 0) {
    return '';
  }

  if (amount < 0) {
    return 'redLabel';
  }

  return 'greenLabel';
};
export const removeNegativeSymbol = (value: number): string => {
  if (value) {
    return value < 0 ? `${value * -1}` : `${value}`;
  }

  return '0.00';
};

export const formatTransferAmount = (value: number | undefined) => {
  if (value === undefined) return '';
  return amountSignedPound(value, 'never');
};

export const cleanAndFormatGBPWithDecimals = (value: string): string => {
  // Remove any non-numeric characters except for '-' and '.'.
  const cleanedValue = value.replace(/[^\d.-]/g, '');

  // Convert to Decimal to avoid floating-point issues.
  const numericValue = new Decimal(cleanedValue);

  // Get the absolute value and format to 2 decimal places.
  const absValue = numericValue.abs().toFixed(2);

  // Check if the original value was negative.
  return numericValue.isNegative() ? `-£${absValue}` : `£${absValue}`;
};
